<template>
  <button class="btn btn-default pull-right" style="margin-left: 10px;"
    @click="$emit('updateparticipants', {participants: checkedParticipants})"
    :disabled="checkedParticipants.length < 1"
    >
    Edit Info
  </button>
  <button class="btn btn-default pull-right"
    @click="showTasksModal" 
    :disabled="checkedParticipants.length < 1"
    >
    Create Tasks
  </button>

  <div class="form-horizontal">
    <div class="form-group">
      <label for="filter" class="control-label col-sm-1"><strong>Filter:</strong></label>
      <div class="col-sm-3">
        <input type="text" id="filter" class="form-control" v-model="filterBuffer" placeholder="Type to Search" autocomplete="off" />
      </div>
    </div>
  </div>

  <table class="table table-striped" id="consent-detail-table">
    <thead>
      <tr>
        <th @click="setSortBy('ccs_id')">
          CCS ID
        </th>
        <th @click="setSortBy('full_name')">
          Name
        </th>
        <th @click="setSortBy('age')">
          Age
        </th>
        <th @click="setSortBy('gender')">
          Gender
        </th>
        <th @click="setSortBy('language')">
          Language
        </th>
        <th @click="setSortBy('sab')">
          SAB
        </th>
        <th @click="setSortBy('serostatus')">
          Serostatus
        </th>
        <th @click="setSortBy('eligibility')">
          Eligibility
        </th>
        <th @click="setSortBy('substudy_status')">
          Substudy Status
        </th>
        <th @click="setSortBy('last_exam')">
          Last Exam
        </th>
        <th>Upcoming Task</th>
        <th>
          <input type="checkbox" id="checkall" @click="checkAll" v-model="allChecked">
        </th>
      </tr>
    </thead>

    <tbody>
      <tr v-for="part in sortedParticipants" :key="'part_'+part.id">
        <td><a :href="'/participants/'+part.id">{{ part.ccs_id }}</a></td>
        <td><a :href="'/participants/'+part.id">{{ part.full_name }}</a></td>
        <td>{{ part.age }}</td>
        <td>{{ part.gender }}</td>
        <td>{{ part.language }}</td>
        <td>{{ part.sab }}</td>
        <td>{{ part.serostatus }}</td>
        <td>{{ part.eligibility ? part.eligibility.name : '' }}</td>
        <td>{{ part.substudy_status ? part.substudy_status.name : '' }}</td>
        <td>{{ part.last_exam ? part.last_exam.completion_date.format('MM/DD/YYYY') : '' }}</td>
        <td>
          <substudy-task-form
            :participant="part"
            :visit-windows="visitWindows"
            :substudy="substudy"
            v-on:taskupdated="$emit('changewindow', $event)"
            v-on:taskcanceled="$emit('canceltask', $event)"
            v-on:taskcreated="$emit('taskcreated', $event)"
            v-if="substudy.active"
          ></substudy-task-form>
        </td>
        <td>
          <input type="checkbox" :id="'checkbox_'+part.id" :value="part.id" v-model="checkedParticipants" number>
        </td>
      </tr>
    </tbody>
  </table>

  <modal v-model:showModal="showNewTasksModal" @closed="error = null">
    <template v-slot:title>Create tasks for selected visit window for checked participants</template>
    <template v-slot:body>
      <div class="alert alert-danger" v-show="error">
        {{ error }}
      </div>
      <div class="form-group">
        <label for="new-window-id">Visit Window</label>
        <select v-model="newTasksVisitWindowId" id="new-window-id" class="form-control" :disabled="creatingTasks">
          <option 
            v-for="(window) in visitWindows"
            :key="window.id"
            :value="window.id"
          >{{window.name}}</option>
        </select>
      </div>
      <button type="button" class="btn btn-default" @click="createNewTasks" :disabled="creatingTasks">
        {{ creatingTasks ? 'Creating...' : 'Create Tasks' }}
      </button>
    </template>
  </modal>
</template>

<script setup>
  import { ref, onMounted, computed, watch } from 'vue'
  import debounce from 'lodash.debounce'
  import Auth from '../auth.js'
  import moment from 'moment'
  import SubstudyTaskForm from './substudies/SubstudyTaskForm.vue'
  import VisitWindowResource from '../resources/VisitWindowResource'
  import SubstudyResource from '../resources/SubstudyResource'
  import Modal from './Modal.vue'

  const props = defineProps(['participants', 'uncheckParticipants', 'showstatus', 'substudy'])
  const emit = defineEmits(['taskscreated', 'updateparticipants', 'taskcreated', 'taskupdated', 'taskcanceled', 'changewindow', 'canceltask'])

  const modalVisible = ref(false)
  const checkedParticipants = ref([])
  const filteredParticipants = ref([])
  const allChecked = ref(false)
  const editingTask = ref({})
  const filterBuffer = ref(null)
  const filter = ref(null)
  const visitWindows = ref([])
  const showNewTasksModal = ref(false)
  const newTasksVisitWindowId = ref(null)
  const error = ref(null)
  const creatingTasks = ref(false)
  const sortBy = ref('ccs_id')
  const sortDir = ref('asc')

  const isAdmin = computed(() => {
    return Auth.isAdmin();
  })

  onMounted(() => {
    fetchVisitWindows();
  })

  function setSortBy(val) {
    sortBy.value = val
    sortDir.value = sortDir.value == 'asc' ? 'desc' : 'asc'
  }

  function fetchVisitWindows() {
    VisitWindowResource.all()
      .then((response)  => {
        visitWindows.value = response.data.filter(function(item){
          return moment(item.closes_at) > moment() && moment(item.closes_at) < moment().add(3.5, 'years');
        });
      });
  }

  function checkAll() {
    checkedParticipants.value = []
    let checked = []
    let parts = sortedParticipants.value

    if (!allChecked.value) {
      parts.forEach(function (participant) {
        checked.push(participant.id)
      })
      checkedParticipants.value = checked
      allChecked.value = true
    } else {
      allChecked.value = false
    }
  }

  function showTasksModal () {
      showNewTasksModal.value = true;
      error.value = null;
  }

  function createNewTasks() {
    // if filter exists, keep only participants who are both checked and filtered
    let checkedIds = []
    if (sortedParticipants.value.length > 0) {
      let checked = sortedParticipants.value.filter(x => checkedParticipants.value.includes(x.id))
      checkedIds = checked.map(x => x.id) 
    } else {
     checkedIds = checkedParticipants.value
    }

    creatingTasks.value = true
    let participantsNewTasks = []
    for (const participantId of checkedIds) {
      participantsNewTasks.push({
        'owner_id': participantId,
        'owner_type': 'App\\Models\\Participant',
        'visit_window_id': newTasksVisitWindowId.value,
        'task_type_id': 15,
        'date_due': moment(visitWindows.value.find((item) => item.id == newTasksVisitWindowId.value).closes_at).format('YYYY-MM-DD'),
        'data': {
          'substudy_id': props.substudy.id,
          'visit_window_id': newTasksVisitWindowId.value
        }
      })
    }
    window.axios.post('/api/bulk-tasks', participantsNewTasks)
      .then(response => {
        emit('taskscreated')
        checkedParticipants.value = []
        showNewTasksModal.value = creatingTasks.value = false
      })
      .catch(error => {
        error.value = error.errors.message;
        creatingTasks.value = false;
      })
  }

  const sortedParticipants = computed(() => {
    var filterFunc = function(part) {
      var re = new RegExp(filter.value, 'i');
      if (filter.value && filter.value.length > 1) {
        return (part.full_name.search(re) != -1
            || part.ccs_id == filter
            || part.age == filter
            || (part.serostatus != null && part.serostatus.search(re) != -1)
            || (part.language != null && part.language.search(re) != -1)
            || (part.sab != null && part.sab.search(re) != -1)
            || (part.gender != null && part.gender.search(re) != -1)
            || (part.eligibility != null && part.eligibility.name.search(re) != -1)
            || (part.substudy_status != null && part.substudy_status.name.search(re) != -1)
            || (part.last_exam && part.last_exam.completion_date.format('m/d/Y').search(re) != -1));
      } else {
        return true
      }
    }.bind(this);

    let direction = sortDir.value == 'asc' ?  1 : -1;
    return props.participants.filter(filterFunc)
        .sort(function (a, b) {
          let sortA = a[sortBy.value]
          let sortB = b[sortBy.value]
          if (sortBy.value == 'substudy_status') {
            sortA = a['substudy_status'] != null ? a['substudy_status']['name'] : ''
            sortB = b['substudy_status'] != null ? b['substudy_status']['name'] : ''
          } 
          if (sortBy.value == 'eligibility') {
            sortA = a['eligibility'] != null ? a['eligibility']['name'] : ''
            sortB = b['eligibility'] != null ? b['eligibility']['name'] : ''
          } 
          if (sortBy.value == 'last_exam') {
            sortA = a['last_exam'] != null ? a['last_exam']['completion_date'] : ''
            sortB = b['last_exam'] != null ? b['last_exam']['completion_date'] : ''
          } 
          if (sortA > sortB) return 1 * direction;
          if (sortA < sortB) return -1 * direction;
          return 0;
        }.bind(this));
  })

  watch(filterBuffer, debounce(() => {
    filter.value = filterBuffer.value;
  }, 500))
</script>

<style scoped>
  th {
    cursor: pointer;
  }
</style>
